import {useMemo, useState} from 'react';

import {FormLabel, Grid} from '@mui/material';
import {
  Radio,
  RadioGroup,
  Stack,
  TextField,
} from '@zoom/react-ui';
import {IconLocationGPS, IconLocationNavigation, IconMSTeamsYes, IconPhone} from '@zoom/sun-ui-icon';
import {useTranslation} from 'react-i18next';

import {ReactComponent as GoogleMeetIcon} from 'assets/svg/google-meet.svg';
import {ReactComponent as ZoomAppIcon} from 'assets/svg/zoom-app-icon.svg';
import {TypographyErrorMsg} from 'Components/BookingBlocksWidget/Style';
import CountryInput from 'Components/CountryInput';
import {ZmTextField} from 'Components/ZmInputs';
import {ADD_ON_TYPE} from 'Utils/consts';

const findSelectedIndex = (configurations, value) => {
  return `${configurations.findIndex((cfg) => {
    if ([ADD_ON_TYPE.CUSTOM, ADD_ON_TYPE.IN_PERSON].includes(cfg.kind)) {
      return value?.location === cfg.location;
    } else {
      return value?.kind === cfg.kind;
    }
  })}`;
};

const RADIO_OPTION_MAPPING = {
  [ADD_ON_TYPE.IN_PERSON]: {
    icon: <IconLocationGPS size={16} />,
  },
  [ADD_ON_TYPE.IN_BOUND_CALL]: {
    icon: <IconPhone size={16} />,
    label: 'apptForm.phoneCall',
  },
  [ADD_ON_TYPE.OUT_BOUND_CALL]: {
    icon: <IconPhone size={16} />,
    label: 'apptForm.phoneCall',
  },
  [ADD_ON_TYPE.ZOOM_MEETING]: {
    icon: <ZoomAppIcon style={{width: '1rem', height: '1rem'}} />,
    label: 'common.zoomMeeting',
  },
  [ADD_ON_TYPE.GOOGLE_MEETING]: {
    icon: <GoogleMeetIcon style={{width: '1rem', height: '1rem'}} />,
    label: 'integration.googleMeetTitle',
  },
  [ADD_ON_TYPE.TEAMS_MEETING]: {
    icon: <IconMSTeamsYes style={{width: '1rem', height: '1rem'}} />,
    label: 'integration.teamsMeetTitle',
  },
  [ADD_ON_TYPE.CUSTOM]: {
    icon: <IconLocationGPS size={16} />,
  },
  [ADD_ON_TYPE.ASK_INVITEE]: {
    icon: <IconLocationNavigation size={16} />,
    label: 'booking.somewhereElse',
  },
};

const getRadioOptionIconAndLabel = (cfg, t) => {
  const {icon, label} = RADIO_OPTION_MAPPING[cfg?.kind] || {};
  return {
    optionIcon: icon,
    optionLabel: label? t(label): (cfg?.location),
  };
};

export const LocationRadioSelector = ({configurations = [], errors, onChange, value}) => {
  const {t} = useTranslation();
  const sortedConfigurations = useMemo(() => {
    // `ask invitee` option should be displayed at the end
    return [...configurations].sort((a, b) => {
      if (a.kind === ADD_ON_TYPE.ASK_INVITEE) {
        return 1;
      } else if (b.kind === ADD_ON_TYPE.ASK_INVITEE) {
        return -1;
      }
      return 0;
    });
  }, [configurations]);

  const [selectedIndex, setSelectedIndex] = useState(findSelectedIndex(sortedConfigurations, value));
  const onlyOneLocation = configurations.length === 1;
  const locationError = errors?.locationConfiguration?.location;

  const label = useMemo(() => {
    let label = '';
    if (onlyOneLocation) {
      if (configurations[0].kind === ADD_ON_TYPE.OUT_BOUND_CALL) {
        label = 'booking.phoneNumber';
      } else if (configurations[0].kind === ADD_ON_TYPE.ASK_INVITEE) {
        label = 'apptForm.locationLabel';
      }
    } else if (configurations.length > 1) {
      label = 'apptForm.locationLabel';
    }
    return label;
  }, [onlyOneLocation, configurations]);

  return label? <>
    <FormLabel required sx={{marginTop: '12px !important', fontWeight: 500, fontSize: '.875rem'}}>
      {t(label)}
    </FormLabel>
    <RadioGroup
      sx={{display: 'flex', marginTop: '8px'}}
      value={selectedIndex}
      onChange={(idx) => {
        const cfg = sortedConfigurations[idx];
        setSelectedIndex(idx);
        onChange({
          kind: cfg.kind,
          location: cfg.location || '',
        });
      }}
    >
      {sortedConfigurations.map((cfg, idx) => {
        const idxStr = `${idx}`;
        const {optionIcon, optionLabel} = getRadioOptionIconAndLabel(cfg, t);
        // if no location or only one location, no need to choose
        return <>{configurations.length > 1 &&
          <Radio value={idxStr}>
            <Stack direction="row" alignItems="center" gap=".3125rem">
              {optionIcon}  {optionLabel}
            </Stack>
          </Radio>
        }
        {cfg.kind === ADD_ON_TYPE.IN_BOUND_CALL && selectedIndex === idxStr &&
          <div style={{fontSize: '.75rem', color: '#777', marginLeft: '42px'}}>
            {t('booking.inviteeCallHostDesc')}
          </div>
        }
        {cfg.kind === ADD_ON_TYPE.OUT_BOUND_CALL && (selectedIndex === idxStr || onlyOneLocation) &&
          <Grid container spacing={2}>
            <Grid item style={{width: '140px'}}>
              <CountryInput
                id={'outboundPhoneCountryCode'}
                hideLabel
                value={value.outboundPhoneCountryCode}
                onChange={(val) => onChange({
                  outboundPhoneCountryCode: val,
                })}
                aria-live="polite"
              />
            </Grid>
            <Grid item xs>
              <ZmTextField margin="dense" id="outboundPhoneNumber" type="text" fullWidth aria-required
                value={value.outboundPhoneNumber}
                variant="outlined"
                onChange={(e) => onChange({
                  outboundPhoneNumber: e.target.value.replace(/[^\d]/g, ''),
                })}
                inputProps={{
                  autoComplete: 'tel-national',
                  minLength: 5,
                  maxLength: 20,
                }}
                aria-live="polite"
                error={!!errors?.locationConfiguration?.outboundPhoneNumber}
                helperText={errors?.locationConfiguration?.outboundPhoneNumber?.message}
              />
            </Grid>
          </Grid>
        }
        {cfg.kind === ADD_ON_TYPE.ASK_INVITEE && (selectedIndex === idxStr || onlyOneLocation) &&
          <TextField
            aria-required
            sx={{width: '310px'}}
            value={value.location}
            onChange={(e) => onChange({
              location: e.target.value,
            })}
            placeholder={t('booking.provideLocationToHost')}
            status={locationError? 'error': undefined}
            errors={locationError? [locationError.message]: undefined}
          />}
        </>;
      })}

      {errors && <TypographyErrorMsg role="alert" aria-live="polite" sx={{marginTop: '4px'}}>
        {errors?.locationConfiguration?.kind?.message}
      </TypographyErrorMsg>}
    </RadioGroup>
  </>: null;
};
